import { useState, useCallback, useRef, useEffect } from "react";
import { elevenLabsStreamingService } from "../services/elevenLabsStreamingService";
import { useVoiceLanguage } from "./useVoiceLanguage";

interface UseSpeechSynthesisReturn {
  speak: (text: string) => Promise<void>;
  beginSession: () => Promise<void>;
  pushText: (chunk: string) => void;
  endSession: () => void;
  preload: (text: string) => void;
  prewarm: () => void;
  stop: () => void;
  isSpeaking: boolean;
  error: Error | null;
}

export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
  const language = useVoiceLanguage();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      elevenLabsStreamingService.stop();
    };
  }, []);

  const sessionCallbacks = useCallback(
    () => ({
      language,
      onStart: () => {
        if (isMountedRef.current) setIsSpeaking(true);
      },
      onEnd: () => {
        if (isMountedRef.current) setIsSpeaking(false);
      },
      onError: (err: Error) => {
        if (isMountedRef.current) {
          setError(err);
          setIsSpeaking(false);
        }
      },
    }),
    [language],
  );

  const speak = useCallback(
    async (text: string): Promise<void> => {
      if (!text.trim()) return;
      setError(null);
      try {
        await elevenLabsStreamingService.speak(text, sessionCallbacks());
      } catch (err) {
        if (isMountedRef.current) {
          const error = err instanceof Error ? err : new Error(String(err));
          setError(error);
          setIsSpeaking(false);
        }
        throw err;
      }
    },
    [sessionCallbacks],
  );

  const beginSession = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await elevenLabsStreamingService.beginSession(sessionCallbacks());
    } catch (err) {
      if (isMountedRef.current) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setIsSpeaking(false);
      }
      throw err;
    }
  }, [sessionCallbacks]);

  const pushText = useCallback((chunk: string): void => {
    elevenLabsStreamingService.pushText(chunk);
  }, []);

  const endSession = useCallback((): void => {
    elevenLabsStreamingService.endSession();
  }, []);

  const stop = useCallback(() => {
    elevenLabsStreamingService.stop();
    if (isMountedRef.current) setIsSpeaking(false);
  }, []);

  // ElevenLabs streams natively via WS — preload and prewarm are no-ops.
  const preload = useCallback((_text: string): void => {
    /* no-op */
  }, []);

  const prewarm = useCallback((): void => {
    /* no-op */
  }, []);

  return {
    speak,
    beginSession,
    pushText,
    endSession,
    preload,
    prewarm,
    stop,
    isSpeaking,
    error,
  };
}
